// import DownloadButton from './DownlaodButton';
import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import CustomTabs from './CustomTabs';
import BarChart from './ BarChart';
import API from '@Services/apiAxios';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';
import { StatsResponse } from './types/type';
import MixedChart from './MIxedChart';
import MixedChartShipment from './MixedChartShipment';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { convertWeightKGtoLB } from '../../utils/convertWeight';

const generateMonthOptions = (): DropdownOptionType[] => {
  return Array.from({ length: 6 }, (_, i) => {
    const date = moment().subtract(i, 'months');
    return {
      value: date.format('MM-YYYY'), // Month-Year format
      display: date.format('MMMM YYYY'), // Full month name with year
    };
  });
};

const monthOptions = generateMonthOptions();

interface StatCardProps {
  icon: string;
  alt: string;
  title: string;
  value: string | number;
  percentage: number;
  description: string;
  percentageLabel: string;
  isLoading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  alt,
  title,
  value,
  percentage,
  description,
  percentageLabel,
  isLoading = false,
}) => {
  const isPositive = percentage >= 0;

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: 'background.paper',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: '60px',
            height: '60px',
            borderRadius: '10px',
            backgroundColor: 'secondary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Image src={icon} alt={alt} style={{ width: '36px', height: '34px' }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: isPositive ? 'success.light' : 'error.light',
              color: isPositive ? 'success.main' : 'error.main',
              fontWeight: 600,
              fontSize: '12px',
              mb: 1.5,
            }}
          >
            <Image
              src={isPositive ? '/images/icons/AdminHome/upArrow.svg' : '/images/icons/AdminHome/downArrow.svg'}
              alt="trend"
              style={{ width: '10px', height: '12px' }}
            />
            {percentageLabel}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '24px', md: '28px' },
              lineHeight: 1.2,
              color: 'text.primary',
              mb: 0.5,
              fontFeatureSettings: '"tnum" on, "lnum" on',
            }}
          >
            {isLoading ? '--' : value}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: 'text.primary',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '13px',
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const GeneralOverview = () => {
  const [system, setSystem] = useState<string[]>([]);
  const locale = useLocale();
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  // const [selectedValues, setSelectedValues] = useState<string[]>([moment().format('MM')]);
  const [selectedValues, setSelectedValues] = useState<{ month: string; year: string }>({
    month: moment().format('MM'),
    year: moment().format('YYYY'),
  });
  const dispatch = useDispatch<AppDispatch>();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const tabLabels = ['Revenue', 'Weight', 'Shipment'];
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const getMonthlyStats = async () => {
    try {
      const response = await API.get('/analytics/stats/monthly', {
        params: {
          month: selectedValues.month,
          year: selectedValues.year,
          'system[]': system,
        },
      });
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
    }
  };

  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, []);

  useEffect(() => {
    getMonthlyStats();
  }, [selectedValues, system]);

  const tabContent = {
    Revenue: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              title="Shipment"
              value={stats?.currentMonth?.totalShipment ? formatNumber(stats.currentMonth.totalShipment, locale) : '--'}
              percentage={stats?.currentMonth?.shipmentPercentage || 0}
              description="Total shipments this month"
              percentageLabel={
                stats?.currentMonth?.shipmentPercentage
                  ? `${formatNumber(stats.currentMonth.shipmentPercentage, locale)}%`
                  : '--%'
              }
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              title="Total Weight"
              value={
                name === 'Cargo Solutions Network'
                  ? stats?.currentMonth?.totalWeight
                    ? formatNumber(convertWeightKGtoLB(stats.currentMonth.totalWeight), locale)
                    : '--'
                  : stats?.currentMonth?.totalWeight
                  ? formatNumber(stats.currentMonth.totalWeight, locale)
                  : '--'
              }
              percentage={stats?.currentMonth?.weightPercentage || 0}
              description={`Total Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`}
              percentageLabel={
                stats?.currentMonth?.weightPercentage
                  ? `${formatNumber(stats.currentMonth.weightPercentage, locale)}%`
                  : '--%'
              }
            />
          </Grid>

          {/* Revenue Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/revenue.png"
              alt="revenue"
              title="Total Revenue"
              value={stats?.currentMonth?.totalRevenue ? formatNumber(stats.currentMonth.totalRevenue, locale) : '--'}
              percentage={stats?.currentMonth?.revenuePercentage || 0}
              description={`Total Revenue (${defaultOrgCountryCurrency})`}
              percentageLabel={
                stats?.currentMonth?.revenuePercentage
                  ? `${formatNumber(stats.currentMonth.revenuePercentage, locale)}%`
                  : '--%'
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <BarChart data={stats?.graphData} />
        </Box>
      </Box>
    ),

    Weight: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              title="Shipment"
              value={stats?.currentMonth?.totalShipment || '--'}
              percentage={stats?.currentMonth?.shipmentPercentage || 0}
              description="Total shipments this month"
              percentageLabel={
                stats?.currentMonth?.shipmentPercentage ? `${stats.currentMonth.shipmentPercentage}%` : '--%'
              }
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              title="Total Weight"
              value={
                name === 'Cargo Solutions Network'
                  ? stats?.currentMonth?.totalWeight
                    ? formatNumber(convertWeightKGtoLB(stats.currentMonth.totalWeight), locale)
                    : '--'
                  : stats?.currentMonth?.totalWeight
                  ? formatNumber(stats.currentMonth.totalWeight, locale)
                  : '--'
              }
              percentage={stats?.currentMonth?.weightPercentage || 0}
              description={`Total Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`}
              percentageLabel={
                stats?.currentMonth?.weightPercentage ? `${stats.currentMonth.weightPercentage}%` : '--%'
              }
            />
          </Grid>

          {/* Revenue Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/revenue.png"
              alt="revenue"
              title="Total Revenue"
              value={stats?.currentMonth?.totalRevenue || '--'}
              percentage={stats?.currentMonth?.revenuePercentage || 0}
              description={`Total Revenue (${defaultOrgCountryCurrency})`}
              percentageLabel={
                stats?.currentMonth?.revenuePercentage ? `${stats.currentMonth.revenuePercentage}%` : '--%'
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <MixedChart data={stats?.graphData} />
        </Box>
      </Box>
    ),

    Shipment: (
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Shipment Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/shipment.png"
              alt="shipment"
              title="Shipment"
              value={stats?.currentMonth?.totalShipment || '--'}
              percentage={stats?.currentMonth?.shipmentPercentage || 0}
              description="Total shipments this month"
              percentageLabel={
                stats?.currentMonth?.shipmentPercentage ? `${stats.currentMonth.shipmentPercentage}%` : '--%'
              }
            />
          </Grid>

          {/* Weight Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/weight.png"
              alt="weight"
              title="Total Weight"
              value={
                name === 'Cargo Solutions Network'
                  ? stats?.currentMonth?.totalWeight
                    ? formatNumber(convertWeightKGtoLB(stats.currentMonth.totalWeight), locale)
                    : '--'
                  : stats?.currentMonth?.totalWeight
                  ? formatNumber(stats.currentMonth.totalWeight, locale)
                  : '--'
              }
              percentage={stats?.currentMonth?.weightPercentage || 0}
              description={`Total Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`}
              percentageLabel={
                stats?.currentMonth?.weightPercentage ? `${stats.currentMonth.weightPercentage}%` : '--%'
              }
            />
          </Grid>

          {/* Revenue Card */}
          <Grid item xs={12} md={4}>
            <StatCard
              icon="/images/icons/AdminHome/revenue.png"
              alt="revenue"
              title="Total Revenue"
              value={stats?.currentMonth?.totalRevenue || '--'}
              percentage={stats?.currentMonth?.revenuePercentage || 0}
              description={`Total Revenue (${defaultOrgCountryCurrency})`}
              percentageLabel={
                stats?.currentMonth?.revenuePercentage ? `${stats.currentMonth.revenuePercentage}%` : '--%'
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <MixedChartShipment data={stats?.graphData} />
        </Box>
      </Box>
    ),
  };

  const handleChange = (event: SelectChangeEvent<`${string}-${string}`>) => {
    const [month, year] = event.target.value.split('-'); // Extract month and year
    setSelectedValues({ month, year });
  };

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };
  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: { xs: '15px 20px', sm: '20px 40px' },
      }}
    >
      <Box
        sx={{
          color: 'rgba(24, 29, 37, 1)',
          fontSize: { xs: '20px', sm: '24px' },
          fontWeight: 600,
          lineHeight: '33.6px',
        }}
      >
        General Overview
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row-reverse' },
            gap: '10px',
            height: 'fit-content',
            position: 'relative',
            top: { xs: '0', sm: '45px' },
          }}
        >
          {/* <Box>
            <DownloadButton
              name={'Download'}
              onClick={() => {
                console.info('click');
              }}
            />
          </Box> */}
          <Box>
            {/* <FilterSelect
              id="month"
              label="Select month"
              options={monthOptions}
              value={selectedValues}
              onChange={handleChange}
            /> */}
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Select month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={`${selectedValues.month}-${selectedValues.year}`} // Set the selected value
                onChange={handleChange}
                label="Select month"
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FilterSelect
              id="system"
              label="Select system"
              options={loggedInUserSystemDropdownList}
              value={system}
              onChange={onSystemFilter}
            />
          </Box>
        </Box>
        <Box
          sx={{
            marginTop: {
              xs: '20px',
              sm: '0px',
            },
          }}
        >
          <CustomTabs tabs={tabLabels} tabContent={tabContent} />
        </Box>
      </Box>
    </Box>
  );
};

export default GeneralOverview;
